/**
 * WebSocket Validation Schemas
 *
 * Zod schemas for validating WebSocket messages and activity data.
 * Types are derived from the generated API types to ensure frontend and backend stay in sync.
 */

import { z } from 'zod';

import {
  API_ACTIVITY_KINDS,
  API_ACTIVITY_SERVICES,
  API_ACTIVITY_STATUSES,
} from '@/services/httpServices/ActivityServices/types';

// ------------- Enums as Zod schemas -------------
// These are derived from the API types to ensure type safety

/**
 * Activity service types that can generate activities
 * Derived from the generated API types
 * UNKNOWN_SERVICE is added for fallback when receiving unknown services
 */
export const ActivityServiceSchema = z.enum([
  ...API_ACTIVITY_SERVICES,
  'UNKNOWN_SERVICE',
]);

/**
 * Activity kind types for all services
 * Derived from the generated API types
 */
export const ActivityKindSchema = z.enum(API_ACTIVITY_KINDS);

/**
 * Activity status values (internal format - uppercase)
 */
export const ActivityStatusSchema = z.enum([
  'IN_PROGRESS',
  'DONE',
  'ERROR',
]);

/**
 * API activity status values (lowercase from backend)
 * Derived from the generated API types
 */
export const ApiActivityStatusSchema = z.enum(API_ACTIVITY_STATUSES);

/**
 * AppSync WebSocket message types
 */
export const AppSyncMessageTypeSchema = z.enum([
  'connection_init',
  'connection_ack',
  'connection_error',
  'ka',
  'subscribe',
  'subscribe_success',
  'subscribe_error',
  'unsubscribe',
  'event',
  'data',
  'error',
]);

// ------------- Status Mapping Helpers -------------

/**
 * Map API status (lowercase) to internal status (uppercase)
 */
function mapApiStatusToInternal(apiStatus: string): 'IN_PROGRESS' | 'DONE' | 'ERROR' {
  switch (apiStatus) {
    case 'inProgress':
      return 'IN_PROGRESS';
    case 'done':
      return 'DONE';
    case 'error':
      return 'ERROR';
    case 'retriggered':
      return 'IN_PROGRESS'; // Treat retriggered as in progress
    default:
      return 'IN_PROGRESS';
  }
}

/**
 * Schema that accepts both uppercase and lowercase status and normalizes to uppercase
 */
const FlexibleStatusSchema = z.union([
  ActivityStatusSchema,
  ApiActivityStatusSchema.transform(mapApiStatusToInternal),
]);

/**
 * Flexible schema that accepts known kinds but also allows unknown strings
 * This ensures we don't break if new kinds are added to the backend
 */
const FlexibleKindSchema = z.string().refine(
  (val): val is ActivityKind | string => val.length > 0,
  { message: 'Kind cannot be empty' },
);

/**
 * Flexible schema that accepts known services but also allows unknown strings
 * This ensures we don't break if new services are added to the backend
 * Unknown services will be normalized to 'UNKNOWN_SERVICE' for display purposes
 */
const FlexibleServiceSchema = z.union([
  ActivityServiceSchema,
  z.string().transform(() => 'UNKNOWN_SERVICE' as const),
]);

// ------------- Activity Schemas -------------

/**
 * Activity metadata schema
 * Validates known properties and strips unknown ones (Zod default behavior)
 * Accepts both uppercase and lowercase status formats
 *
 * Different services have different meta fields (from api.d.ts UserActivityListResponse):
 * - TRANSLATION_SERVICE: { status, spaceName, title, failedLanguages?, requestedGlossaryTranslationFailedLanguages? }
 * - COPY_SERVICE: { status, sourceSpaceName, targetSpaceName }
 * - VOICE_SERVICE: { status, spaceName }
 * - PUBLISH_SERVICE: { status, spaceName, title }
 * - DOC2COURSE (GENERATE_COURSE_STRUCTURE): { status, courseStructureId, spaceName, courseTitle }
 * - DOC2COURSE (GENERATE_COURSE): { status, courseId, spaceName, courseTitle }
 */
export const ActivityMetaSchema = z.object({
  status: FlexibleStatusSchema,
  // Common fields
  spaceName: z.string().optional(),
  // TRANSLATION_SERVICE specific fields
  title: z.string().optional(),
  failedLanguages: z.array(z.string()).optional(),
  requestedGlossaryTranslationFailedLanguages: z.array(z.string()).optional(),
  // COPY_SERVICE specific fields
  sourceSpaceName: z.string().optional(),
  targetSpaceName: z.string().optional(),
  // DOC2COURSE specific fields
  courseTitle: z.string().optional(),
  courseId: z.string().optional(),
  courseStructureId: z.string().optional(),
});

/**
 * Initial job schema for activities
 * Contains resourceId which can be used for linking to entities
 */
export const ActivityInitialJobSchema = z.object({
  id: z.string().optional(),
  kind: z.string().optional(),
  initiatedByUid: z.string().optional(),
  resourceId: z.string().optional(),
});

/**
 * Default finishedAt to null when the upstream payload omits the field entirely.
 *
 * The IDA backend serialises in-progress activities with Pydantic's
 * `model_dump(exclude_none=True)`, which strips `finishedAt` from the JSON
 * instead of emitting `null`. The TS coach-server emits `finishedAt: null`
 * explicitly. We accept both so notifications work for every publisher.
 */
const FinishedAtSchema = z.string().nullable().optional().transform(value => value ?? null);

/**
 * Activity data in internal format (with userId)
 * Uses FlexibleServiceSchema to handle unknown services gracefully
 */
const InternalActivityDataSchema = z.object({
  id: z.string(),
  userId: z.string(),
  service: FlexibleServiceSchema,
  kind: FlexibleKindSchema,
  startedAt: z.string(),
  finishedAt: FinishedAtSchema,
  status: FlexibleStatusSchema,
  meta: ActivityMetaSchema,
  initialJob: ActivityInitialJobSchema.optional(),
});

/**
 * Activity data in API format (with optional initialJob containing initiatedByUid)
 * Uses FlexibleServiceSchema to handle unknown services gracefully
 */
const ApiActivityDataSchema = z.object({
  id: z.string(),
  service: FlexibleServiceSchema,
  kind: FlexibleKindSchema,
  startedAt: z.string(),
  finishedAt: FinishedAtSchema,
  meta: ActivityMetaSchema,
  initialJob: ActivityInitialJobSchema.optional(),
}).transform(data => ({
  id: data.id,
  userId: data.initialJob?.initiatedByUid ?? '',
  service: data.service,
  kind: data.kind,
  startedAt: data.startedAt,
  finishedAt: data.finishedAt,
  status: data.meta.status,
  meta: data.meta,
  initialJob: data.initialJob,
}));

/**
 * Activity data received from WebSocket
 * Accepts both internal format (with userId) and API format (with initialJob)
 */
export const ActivityDataSchema = z.union([
  InternalActivityDataSchema,
  ApiActivityDataSchema,
]);

// ------------- Event Schemas -------------

/**
 * Activity updated event from WebSocket (wrapped format)
 * Format: { type: "ActivityUpdated", data: ActivityData }
 */
export const ActivityUpdatedEventSchema = z.object({
  type: z.literal('ActivityUpdated'),
  data: ActivityDataSchema,
});

/**
 * Direct activity data event from WebSocket (unwrapped format)
 * Some backends send just the activity data without a wrapper.
 * We normalize this to the ActivityUpdatedEvent format.
 */
export const DirectActivityDataSchema = ActivityDataSchema.transform(data => ({
  type: 'ActivityUpdated' as const,
  data,
}));

/**
 * All possible event types from WebSocket
 * Supports both wrapped format { type, data } and direct activity data
 */
export const WebSocketEventSchema = z.union([
  ActivityUpdatedEventSchema,
  DirectActivityDataSchema,
]);

// ------------- AppSync Message Schemas -------------

/**
 * Connection ack message (received from server)
 */
export const AppSyncConnectionAckMessageSchema = z.object({
  type: z.literal('connection_ack'),
  connectionTimeoutMs: z.number().optional(),
});

/**
 * Connection error message (received from server)
 */
export const AppSyncConnectionErrorMessageSchema = z.object({
  type: z.literal('connection_error'),
  errors: z.array(z.object({
    errorCode: z.number(),
    message: z.string(),
  })).optional(),
});

/**
 * Keep-alive message (received from server)
 */
export const AppSyncKeepAliveMessageSchema = z.object({
  type: z.literal('ka'),
});

/**
 * Subscribe success message (received from server)
 */
export const AppSyncSubscribeSuccessMessageSchema = z.object({
  type: z.literal('subscribe_success'),
  id: z.string(),
});

/**
 * Subscribe error message (received from server)
 */
export const AppSyncSubscribeErrorMessageSchema = z.object({
  type: z.literal('subscribe_error'),
  id: z.string(),
  errors: z.array(z.object({
    errorType: z.string(),
    message: z.string(),
  })).optional(),
});

/**
 * Event message (received from server)
 */
export const AppSyncEventMessageSchema = z.object({
  type: z.literal('event'),
  id: z.string(),
  event: z.string(),
});

/**
 * Data message (received from server)
 */
export const AppSyncDataMessageSchema = z.object({
  type: z.literal('data'),
  id: z.string(),
  event: z.string(),
});

/**
 * Error message (received from server)
 */
export const AppSyncErrorMessageSchema = z.object({
  type: z.literal('error'),
  errors: z.array(z.object({
    errorType: z.string(),
    message: z.string(),
  })).optional(),
});

/**
 * All possible AppSync messages received from server
 * Discriminated union based on 'type' field
 */
export const AppSyncIncomingMessageSchema = z.discriminatedUnion('type', [
  AppSyncConnectionAckMessageSchema,
  AppSyncConnectionErrorMessageSchema,
  AppSyncKeepAliveMessageSchema,
  AppSyncSubscribeSuccessMessageSchema,
  AppSyncSubscribeErrorMessageSchema,
  AppSyncEventMessageSchema,
  AppSyncDataMessageSchema,
  AppSyncErrorMessageSchema,
]);

// ------------- Outgoing Message Schemas -------------

/**
 * Connection init message (sent by client)
 */
export const AppSyncConnectionInitMessageSchema = z.object({
  type: z.literal('connection_init'),
});

/**
 * Subscribe message (sent by client)
 */
export const AppSyncSubscribeMessageSchema = z.object({
  type: z.literal('subscribe'),
  id: z.string(),
  channel: z.string(),
  authorization: z.object({
    host: z.string(),
    authorization: z.string(),
  }),
});

/**
 * Unsubscribe message (sent by client)
 */
export const AppSyncUnsubscribeMessageSchema = z.object({
  type: z.literal('unsubscribe'),
  id: z.string(),
});

/**
 * All possible AppSync messages sent by client
 */
export const AppSyncOutgoingMessageSchema = z.union([
  AppSyncConnectionInitMessageSchema,
  AppSyncSubscribeMessageSchema,
  AppSyncUnsubscribeMessageSchema,
]);

// ------------- Enum Value Constants -------------

/**
 * Activity status values as const object for type-safe comparisons
 * Usage: ActivityStatusValues.IN_PROGRESS, ActivityStatusValues.DONE, etc.
 */
export const ActivityStatusValues = ActivityStatusSchema.enum;

/**
 * Activity service values as const object for type-safe comparisons
 * Usage: ActivityServiceValues.TRANSLATION_SERVICE, etc.
 */
export const ActivityServiceValues = ActivityServiceSchema.enum;

/**
 * Activity kind values as const object for type-safe comparisons
 * Usage: ActivityKindValues.TRANSLATE_COURSE, ActivityKindValues.PUBLISH_SEQUENCE, etc.
 */
export const ActivityKindValues = ActivityKindSchema.enum;

/**
 * AppSync message type values as const object for type-safe comparisons
 */
export const AppSyncMessageTypeValues = AppSyncMessageTypeSchema.enum;

// ------------- Inferred Types -------------

export type ActivityService = z.infer<typeof ActivityServiceSchema>;
export type ActivityStatus = z.infer<typeof ActivityStatusSchema>;
export type ActivityKind = z.infer<typeof ActivityKindSchema>;
export type AppSyncMessageType = z.infer<typeof AppSyncMessageTypeSchema>;
export type ActivityMeta = z.infer<typeof ActivityMetaSchema>;
export type ActivityInitialJob = z.infer<typeof ActivityInitialJobSchema>;
export type ActivityData = z.infer<typeof ActivityDataSchema>;
export type ActivityUpdatedEvent = z.infer<typeof ActivityUpdatedEventSchema>;
export type WebSocketEvent = z.infer<typeof WebSocketEventSchema>;
export type AppSyncConnectionAckMessage = z.infer<typeof AppSyncConnectionAckMessageSchema>;
export type AppSyncConnectionErrorMessage = z.infer<typeof AppSyncConnectionErrorMessageSchema>;
export type AppSyncKeepAliveMessage = z.infer<typeof AppSyncKeepAliveMessageSchema>;
export type AppSyncSubscribeSuccessMessage = z.infer<typeof AppSyncSubscribeSuccessMessageSchema>;
export type AppSyncSubscribeErrorMessage = z.infer<typeof AppSyncSubscribeErrorMessageSchema>;
export type AppSyncEventMessage = z.infer<typeof AppSyncEventMessageSchema>;
export type AppSyncDataMessage = z.infer<typeof AppSyncDataMessageSchema>;
export type AppSyncErrorMessage = z.infer<typeof AppSyncErrorMessageSchema>;
export type AppSyncIncomingMessage = z.infer<typeof AppSyncIncomingMessageSchema>;
export type AppSyncConnectionInitMessage = z.infer<typeof AppSyncConnectionInitMessageSchema>;
export type AppSyncSubscribeMessage = z.infer<typeof AppSyncSubscribeMessageSchema>;
export type AppSyncUnsubscribeMessage = z.infer<typeof AppSyncUnsubscribeMessageSchema>;
export type AppSyncOutgoingMessage = z.infer<typeof AppSyncOutgoingMessageSchema>;

// ------------- Validation Functions -------------

/**
 * Parse and validate an incoming WebSocket message
 * Returns null if validation fails
 */
export function parseIncomingMessage(data: string): AppSyncIncomingMessage | null {
  try {
    const json: unknown = JSON.parse(data);
    const result = AppSyncIncomingMessageSchema.safeParse(json);

    if (result.success === false) {
      return null;
    }

    return result.data;
  } catch {
    return null;
  }
}

/**
 * Parse and validate event data from AppSync event message
 * Returns null if validation fails
 */
export function parseEventData(eventString: string): WebSocketEvent | null {
  try {
    const json: unknown = JSON.parse(eventString);
    const result = WebSocketEventSchema.safeParse(json);

    if (result.success === false) {
      return null;
    }

    return result.data;
  } catch {
    return null;
  }
}

// ------------- API Activity Mapping -------------

/**
 * API activity format from user-management service
 */
type ApiActivity = {
  id: string;
  service: string;
  kind: string;
  startedAt: string;
  finishedAt: string | null;
  meta: {
    status: 'inProgress' | 'done' | 'error' | 'retriggered';
    spaceName?: string;
    title?: string;
    failedLanguages?: string[];
    [key: string]: unknown;
  };
  initialJob?: {
    initiatedByUid?: string;
    [key: string]: unknown;
  };
};

/**
 * Map API activity format to internal ActivityData format
 * Used when loading initial activities from the API
 * Unknown services are normalized to 'UNKNOWN_SERVICE' instead of being rejected
 */
export function mapApiActivityToActivityData(apiActivity: ApiActivity): ActivityData | null {
  try {
    // Map service name - use known service or fallback to UNKNOWN_SERVICE
    const serviceResult = ActivityServiceSchema.safeParse(apiActivity.service);
    const service: ActivityService = serviceResult.success === true
      ? serviceResult.data
      : 'UNKNOWN_SERVICE';

    // Map status from meta.status (lowercase) to internal format (uppercase)
    const status = mapApiStatusToInternal(apiActivity.meta.status);

    // Get userId from initialJob if available
    const userId = apiActivity.initialJob?.initiatedByUid ?? '';

    // Preserve all meta fields from the API, normalizing the status
    const activityData: ActivityData = {
      id: apiActivity.id,
      userId,
      service,
      kind: apiActivity.kind,
      startedAt: apiActivity.startedAt,
      finishedAt: apiActivity.finishedAt,
      status,
      meta: {
        ...apiActivity.meta,
        status, // Override with normalized status
      },
    };

    return activityData;
  } catch {
    return null;
  }
}
